import { HttpParams } from '@angular/common/http';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { environment } from 'src/environments/env';
export default class Utils {

  // method to convert object to string for Filter purpose to pass to get Api
  createFilterString(filterObj: any) {
    let filterString = "&";
    const entries = Object.entries(filterObj);
    const validEntries = entries.filter(([key, value]) => value || value === false);

    for (let i = 0; i < validEntries.length; i++) {
      const [key, value] = validEntries[i];
      const isLastIteration = i === validEntries.length - 1;

      if (typeof (value) == 'object') {
        // value is date
        const encodedValue = encodeURIComponent(this.isoDateWithoutTimeZone(value));
        filterString = filterString + `${encodeURIComponent(key)}=${encodedValue}${isLastIteration ? '' : '&'}`;
      } else {
        const encodedValue = encodeURIComponent(String(value));
        filterString = filterString + `${encodeURIComponent(key)}=${encodedValue}${isLastIteration ? '' : '&'}`;
      }
    }
    return filterString == '&' ? '' : filterString;
  }


  sanitizeFileName(name: string): string {
    if (!name) return 'download.pdf';

    // Remove dangerous characters
    let safeName = name
      .replace(/[<>"'%;()&+]/g, '')        // Remove XSS-prone chars
      .replace(/\s+/g, '_')                // Replace spaces with _
      .replace(/[^\w.\-]/g, '')            // Allow only letters, numbers, _, -, .
      .trim();

    // Enforce .pdf extension
    if (!safeName.toLowerCase().endsWith('.pdf')) {
      safeName += '.pdf';
    }

    // Limit length
    return safeName.substring(0, 100);
  }


  isoDateWithoutTimeZone(date: any) {
    if (date == null) return date;
    let timestamp = date.getTime() - date.getTimezoneOffset() * 60000;
    let correctDate = new Date(timestamp);
    // removing the Miliseconds from date
    return correctDate.toISOString().replace(/.\d+Z$/g, "Z");;
  }


  checkForSubstring(masterString: any) {
    return masterString.includes('login') ? 1 : masterString.includes('email') ? 2 : 3
  }


  mapRenewalStatusFilter(filterString: string): string {
    const renewalStatusMap: Record<string, string> = {
      'Renewal Submitted': 'RENEWAL',
      'Renewal Assessment': 'RENEWAL_EXAM',
      'Renewal Pre Approved': 'RENEWAL_APPROVE',
      'Renewal Rejected': 'RENEWAL_REJECT',
      'Renewal ILT Failed': 'RENEWAL_ILT_FAIL',
      'Renewal ILT Submitted': 'RENEWAL_ILT_SUBMIT',
      'In Renewal ILT Training': 'RENEWAL_ILT_Payment',
      'Renewal Free Assessment Failed': 'RENEWAL_TEST_OUT_OPEN',
      'Renewal TestOut Passed': 'RENEWAL_TEST_OUT_PASS',
      'Renewal TestOut Failed': 'RENEWAL_TEST_OUT_FAIL',
      'In Renewal TestOut Training': 'RENEWAL_TEST_OUT_PAYMENT'
    };

    //&status=Renewal%20Submitted" - sample Input of filterString
    const statusMatch = filterString.match(/(^|&)status=([^&]*)/);

    if (!statusMatch) {
      return filterString;
    }

    let status: string;
    try {
      status = decodeURIComponent(statusMatch[2]);
    } catch {
      return filterString;
    }

    const mappedStatus = renewalStatusMap[status];
    return mappedStatus
      ? filterString.replace(statusMatch[0], `${statusMatch[1]}status=${mappedStatus}`)
      : filterString;
  }

  // Dynamic function to format dates
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${month}/${day}/${year}`;
  }


  // Sanitize filter string to prevent injection attacks
  sanitizeFilterString(filterString: string): string {
    if (!filterString || filterString.trim() === '') {
      return '';
    }

    // Parse the filter string and re-encode each parameter
    try {
      // Remove leading '&' if present
      const cleanString = filterString.startsWith('&') ? filterString.substring(1) : filterString;

      if (!cleanString) {
        return '';
      }

      // Split by '&' to get individual parameters
      const params = cleanString.split('&');
      const sanitizedParams: string[] = [];

      for (const param of params) {
        const [key, value] = param.split('=');
        if (key && value !== undefined) {
          // Encode both key and value to prevent injection
          const encodedKey = encodeURIComponent(key.trim());
          const encodedValue = encodeURIComponent(value);
          sanitizedParams.push(`${encodedKey}=${encodedValue}`);
        }
      }

      // Return with leading '&' if we have parameters
      return sanitizedParams.length > 0 ? '&' + sanitizedParams.join('&') : '';
    } catch (error) {
      console.error('Error sanitizing filter string:', error);
      return ''; // Return empty string if sanitization fails
    }
  }


  // Helper method to parse filter string into key-value pairs
  parseFilterString(filterString: string): any {
    const params: any = {};
    if (!filterString) return params;

    if (filterString.startsWith('&')) {
      filterString = filterString.substring(1); // Remove leading &
    }

    const pairs = filterString.split('&');
    pairs.forEach(pair => {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });

    return params;
  }

  // Optimized method to build HttpParams for API requests with pagination, filtering, and sorting
  buildHttpParams(options: {
    view?: string;
    page?: number | string;
    size?: number | string;
    filterString?: string;
    sort?: { isActive: boolean; direction: string; column: string };
  }): HttpParams {
    let params = new HttpParams();

    // Add basic parameters
    if (options.view) {
      params = params.set('view', options.view);
    }
    if (options.page !== undefined) {
      params = params.set('page', options.page.toString());
    }
    if (options.size !== undefined) {
      params = params.set('size', options.size.toString());
    }

    // Add filter parameters if they exist
    if (options.filterString) {
      const filterParams = this.parseFilterString(options.filterString);
      Object.keys(filterParams).forEach(key => {
        params = params.set(key, filterParams[key]);
      });
    }

    if (options.sort && options.sort.isActive && options.sort.direction) {
      params = params
        .set('sort', options.sort.direction)
        .set('sortBy', options.sort.column);
    }

    return params;
  }
 
  dateDifferenceInDays(start: Date, endDate: string): number {
    const startDate = new Date(start);
    const end = new Date(endDate);
    const diffTime = Math.abs(startDate.getTime() - end.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  logoutUser(): void {
    localStorage.clear();
    sessionStorage.clear();

    setTimeout(() => { window.open(environment.BASE_URL + "/ptp/security/ssoLogout?entryPoint=" + environment.APP_URL + "/#/prelogin", '_self') }, 500)
  }

  customEmailValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    if (!email) {
      return null; // Don't validate empty values, let required validator handle that
    }

    // More strict email regex pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(email)) {
      return { invalidEmail: true };
    }

    return null;
  }


  generateRandomString(length: number = 16): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';

    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

}
